<template>
  <div class="submit-tool-page bg-neutral-100 min-h-screen">
    <section class="py-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="max-w-3xl mx-auto bg-white rounded-xl p-6 md:p-8 border border-neutral-200 shadow-sm"
        >
          <div class="text-center mb-8">
            <h2 class="text-2xl md:text-3xl font-bold mb-4">发布你的AI工具</h2>
            <p class="text-neutral-500">
              分享你创建的AI工具，获得更多曝光和创值奖励
            </p>
            <!-- 开发者等级：按累计发布作品数 -->
            <div
              v-if="levelInfo"
              class="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs"
            >
              <span
                class="px-2 py-0.5 rounded-full border font-medium"
                style="background: #e8f2ff; color: #0b5fd0; border-color: #b6d4ff"
              >
                <i class="fa fa-code mr-1"></i>LV.{{ levelInfo.devLevel }}
                {{ levelInfo.devLevelName }}
              </span>
              <span class="text-neutral-500">
                累计智点 {{ levelInfo.devPoint || 0 }}<template
                  v-if="levelInfo.devNext > 0"
                >
                  ，再获得
                  <b class="text-primary">{{ levelInfo.devNext }}</b>
                  智点升至 LV.{{ levelInfo.devLevel + 1 }}</template
                ><template v-else>，已是最高等级</template>
              </span>
            </div>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label
                  for="tool-name"
                  class="block text-sm font-medium text-neutral-700 mb-1"
                  >工具名称</label
                >
                <input
                  type="text"
                  id="tool-name"
                  v-model="form.title"
                  required
                  class="w-full px-4 py-2 rounded-md border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary apple-transition"
                  placeholder="输入你的工具名称"
                />
              </div>

              <div>
                <label
                  for="tool-category"
                  class="block text-sm font-medium text-neutral-700 mb-1"
                  >工具分类</label
                >
                <div class="relative w-full">
                  <select
                    id="tool-category"
                    v-model="form.categoryid"
                    required
                    class="w-full px-4 py-2 rounded-md border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary apple-transition appearance-none bg-white"
                  >
                    <option
                      v-for="item in categories"
                      :value="item.id"
                      :key="item.id"
                    >
                      {{ item.title }}
                    </option>
                  </select>
                  <!-- 自定义下拉箭头（解决 Safari 默认箭头样式问题） -->
                  <div
                    class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <svg
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L6 6L11 1"
                        stroke="#666"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label
                for="tool-description"
                class="block text-sm font-medium text-neutral-700 mb-1"
                >所属学科</label
              >

              <div class="flex flex-wrap gap-3">
                <div
                  v-for="fmt in subList"
                  :key="fmt.id"
                  @click="setSub(fmt.id)"
                  class="px-4 py-2.5 rounded-2xl text-sm font-bold transition-all duration-200 border"
                  :class="
                    form.subid == fmt.id
                      ? 'bg-stone-800 border-stone-800 text-white shadow-lg shadow-stone-800/30'
                      : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
                  "
                >
                  {{ fmt.title }}
                </div>
              </div>
            </div>

            <!-- 学科知识体系：年段/年级/学期/单元（可选） -->
            <div v-if="form.subid && stageOptions.length > 0">
              <label class="block text-sm font-medium text-neutral-700 mb-1"
                >知识体系（可选）</label
              >
              <p class="text-xs text-neutral-400 mb-2">
                可精确到单元，方便他人按「学科-年段-年级-学期-单元」检索，选择后逐级出现下级选项
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label class="block text-xs text-neutral-500 mb-1">年段</label>
                  <select
                    v-model="form.stageId"
                    @change="onStageChange"
                    class="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                  >
                    <option value="">不限</option>
                    <option v-for="n in stageOptions" :key="n.id" :value="n.id">{{ n.title }}</option>
                  </select>
                </div>
                <div v-if="form.stageId">
                  <label class="block text-xs text-neutral-500 mb-1">年级</label>
                  <select
                    v-model="form.gradeId"
                    @change="onGradeChange"
                    class="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                  >
                    <option value="">不限</option>
                    <option v-for="n in gradeOptions" :key="n.id" :value="n.id">{{ n.title }}</option>
                  </select>
                </div>
                <div v-if="form.gradeId">
                  <label class="block text-xs text-neutral-500 mb-1">学期</label>
                  <select
                    v-model="form.termId"
                    @change="onTermChange"
                    class="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                  >
                    <option value="">不限</option>
                    <option v-for="n in termOptions" :key="n.id" :value="n.id">{{ n.title }}</option>
                  </select>
                </div>
                <div v-if="form.termId">
                  <label class="block text-xs text-neutral-500 mb-1">单元</label>
                  <select
                    v-model="form.unitId"
                    class="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                  >
                    <option value="">不限</option>
                    <option v-for="n in unitOptions" :key="n.id" :value="n.id">{{ n.title }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label
                for="tool-description"
                class="block text-sm font-medium text-neutral-700 mb-1"
                >工具描述</label
              >
              <textarea
                id="tool-description"
                v-model="form.description"
                required
                rows="4"
                class="w-full px-4 py-2 rounded-md border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary apple-transition"
                placeholder="详细描述你的工具功能、使用方法和特点"
              ></textarea>
            </div>

            <div>
              <label
                for="tool-author"
                class="block text-sm font-medium text-neutral-700 mb-1"
                >作者署名（选填）</label
              >
              <input
                id="tool-author"
                v-model="form.authorName"
                type="text"
                maxlength="32"
                class="w-full px-4 py-2 rounded-md border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary apple-transition"
                placeholder="代他人上传时填写对方名字，留空显示为你自己的名字"
              />
              <p class="mt-1 text-xs text-neutral-400">
                帮其他老师代上传工具时，在这里填写 TA 的名字，工具会显示为 TA 的作品
              </p>
            </div>

            <!-- <div>
              <label
                for="tool-url"
                class="block text-sm font-medium text-neutral-700 mb-1"
                >工具访问链接</label
              >
              <input
                type="url"
                id="tool-url"
                v-model="form.link"
                required
                class="w-full px-4 py-2 rounded-md border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary apple-transition"
                placeholder="https://your-tool-url.com"
              />
            </div> -->

            <div>
              <label class="block text-sm font-medium text-neutral-700 mb-1"
                >工具封面图</label
              >
              <div class="flex items-center justify-center w-full">
                <!-- 图片预览区域 -->
                <div v-if="form.coverPreview" class="relative w-full h-32 mb-4">
                  <img
                    :src="form.coverPreview"
                    alt="封面预览"
                    class="w-full h-full object-cover rounded-md border border-neutral-200"
                  />
                  <button
                    type="button"
                    @click="clearCover"
                    class="absolute top-2 right-2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black/70 transition-colors"
                  >
                    ×
                  </button>
                </div>

                <!-- 上传区域 -->
                <label
                  v-else
                  for="file-upload"
                  class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 rounded-md cursor-pointer bg-neutral-50 hover:bg-neutral-100 apple-transition"
                >
                  <div
                    class="flex flex-col items-center justify-center pt-5 pb-6"
                  >
                    <i class="fa fa-cloud-upload text-neutral-400 mb-2"></i>
                    <p class="mb-2 text-sm text-neutral-500">
                      <span class="font-medium">点击上传</span> 或拖放文件
                    </p>
                    <p class="text-xs text-neutral-500">
                      支持 PNG, JPG 或 GIF (最大 {{ uploadCfg.imageMaxMB }}MB)
                    </p>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    class="hidden"
                    accept="image/*"
                    @change="handleFileUpload"
                  />
                </label>
              </div>
              <!-- 上传状态提示 -->
              <div v-if="uploading" class="mt-2">
                <div class="flex items-center justify-between text-sm text-primary mb-1">
                  <span>图片上传中... {{ uploadPercent }}%</span>
                </div>
                <div
                  class="w-full h-2 bg-neutral-200 rounded-full overflow-hidden"
                >
                  <div
                    class="h-full bg-primary rounded-full transition-all duration-200"
                    :style="{ width: uploadPercent + '%' }"
                  ></div>
                </div>
              </div>
              <div v-if="uploadError" class="mt-2 text-sm text-red-500">
                {{ uploadError }}
              </div>
            </div>

            <!-- 新增：ZIP附件上传区域 -->
            <div>
              <label class="block text-sm font-medium text-neutral-700 mb-1"
                >工具附件 (仅支持ZIP压缩包)</label
              >
              <div class="flex items-center justify-center w-full">
                <!-- 附件文件预览区域 -->
                <div
                  v-if="form.zipFileName"
                  class="relative w-full mb-4 bg-neutral-50 rounded-md border border-neutral-200 p-3 flex items-center justify-between"
                >
                  <div class="flex items-center">
                    <i class="fa fa-file-archive-o text-primary mr-2"></i>
                    <span class="text-sm text-neutral-700 truncate">{{
                      form.zipFileName
                    }}</span>
                    <span class="ml-2 text-xs text-neutral-500"
                      >({{ formatFileSize(form.zipFileSize) }})</span
                    >
                  </div>
                  <button
                    type="button"
                    @click="clearZipFile"
                    class="text-neutral-500 hover:text-red-500 transition-colors"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </div>

                <!-- 附件上传区域 -->
                <label
                  v-else
                  for="zip-file-upload"
                  class="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-neutral-300 rounded-md cursor-pointer bg-neutral-50 hover:bg-neutral-100 apple-transition"
                >
                  <div
                    class="flex flex-col items-center justify-center pt-5 pb-6"
                  >
                    <i class="fa fa-file-zip-o text-neutral-400 mb-2"></i>
                    <p class="mb-2 text-sm text-neutral-500">
                      <span class="font-medium">点击上传</span> 或拖放ZIP/HTML文件
                    </p>
                    <p class="text-xs text-neutral-500">
                      单个文件最大 {{ uploadCfg.fileMaxMB }}MB，ZIP 可打包视频/音乐等素材（需包含 index.html）
                    </p>
                  </div>
                  <input
                    id="zip-file-upload"
                    type="file"
                    class="hidden"
                    accept=".zip,application/zip,.html,text/html"
                    @change="handleZipFileUpload"
                  />
                </label>
              </div>
              <!-- 附件上传状态提示 -->
              <div v-if="zipUploading" class="mt-2">
                <div class="flex items-center justify-between text-sm text-primary mb-1">
                  <span>附件上传中... {{ zipUploadPercent }}%</span>
                  <span class="text-xs text-neutral-400">{{
                    formatFileSize(form.zipFileSize)
                  }}</span>
                </div>
                <div
                  class="w-full h-2 bg-neutral-200 rounded-full overflow-hidden"
                >
                  <div
                    class="h-full bg-primary rounded-full transition-all duration-200"
                    :style="{ width: zipUploadPercent + '%' }"
                  ></div>
                </div>
              </div>
              <div v-if="zipUploadError" class="mt-2 text-sm text-red-500">
                {{ zipUploadError }}
              </div>
            </div>

            <div class="pt-2">
              <button
                type="submit"
                :disabled="uploading || zipUploading || !form.title"
                class="w-full px-6 py-3 rounded-md bg-primary text-white font-medium hover:bg-primary/90 apple-transition shadow-sm disabled:bg-neutral-300 disabled:cursor-not-allowed"
              >
                发布工具
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { requireLogin } from "../utils/authGuard";
import {
  getUploadConfig,
  imageMaxBytes,
  fileMaxBytes,
} from "@/utils/uploadConfig";

export default {
  name: "SubmitToolPage",
  data() {
    return {
      /** 开发者等级（按已发布作品数） */
      levelInfo: null,
      categories: [],
      subList: [],
      eduList: [],
      form: {
        id: "",
        title: "",
        subid: "",
        categoryid: "",
        description: "",
        authorName: "",
        link: "",
        stageId: "",
        gradeId: "",
        termId: "",
        unitId: "",
        cover: "", // 存储服务器返回的图片URL
        coverPreview: "", // 存储本地预览图片的base64
        imageFile: null, // 存储选中的封面文件对象

        // 新增：ZIP附件相关字段
        zipFile: null, // 存储选中的ZIP文件对象
        zipFileName: "", // 存储ZIP文件名
        zipFileSize: 0, // 存储ZIP文件大小(字节)
        zipUrl: "", // 存储服务器返回的ZIP文件URL
      },
      uploading: false, // 封面上传状态
      uploadError: "", // 封面上传错误信息
      uploadPercent: 0, // 封面上传进度

      // 新增：ZIP上传状态
      zipUploading: false, // ZIP上传状态
      zipUploadError: "", // ZIP上传错误信息
      zipUploadPercent: 0, // ZIP上传进度
    };
  },
  computed: {
    // 后台配置的上传大小限制（用于页面上的提示文案）
    uploadCfg() {
      return getUploadConfig();
    },
    // 当前学科下的年段
    stageOptions() {
      return this.eduList.filter(
        (n) => n.type === "stage" && n.parentId === this.form.subid
      );
    },
    gradeOptions() {
      return this.eduList.filter(
        (n) => n.type === "grade" && n.parentId === this.form.stageId
      );
    },
    termOptions() {
      return this.eduList.filter(
        (n) => n.type === "term" && n.parentId === this.form.gradeId
      );
    },
    unitOptions() {
      return this.eduList.filter(
        (n) => n.type === "unit" && n.parentId === this.form.termId
      );
    },
  },
  created() {
    // 访客模式：发布工具需要登录（防止直接访问 URL 绕过入口拦截）
    if (!requireLogin(this, "发布工具")) return;
    const editId = this.$route.query.id;
    this.getCategory().then(() => {
      if (editId) this.loadToolDetail(editId);
    });
    this.loadLevel();
  },
  methods: {
    /** 当前开发者等级（发布作品数决定） */
    loadLevel() {
      this.$axios({ url: "user/level" })
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.levelInfo = res.data;
          }
        })
        .catch(() => {});
    },
    // 编辑：回填自己发布的工具
    async loadToolDetail(id) {
      try {
        const me = (this.$store && this.$store.state.user) || {};
        const res = await this.$axios({ url: `aitool/${id}?uid=${me.uid || ""}` });
        const d = res.code === 200 ? res.data : null;
        if (!d) {
          alert("工具不存在或已删除");
          return;
        }
        if (d.uid && me.uid && String(d.uid) !== String(me.uid)) {
          alert("只能修改自己发布的工具");
          this.$router.replace("/submit");
          return;
        }
        this.form.id = d.id || "";
        this.form.title = d.title || "";
        this.form.categoryid = d.categoryid || "";
        this.form.description = d.description || "";
        this.form.authorName = d.authorName || "";
        this.form.link = d.link || "";
        this.form.subid = d.subid || this.form.subid;
        this.form.stageId = d.stageId || "";
        this.form.gradeId = d.gradeId || "";
        this.form.termId = d.termId || "";
        this.form.unitId = d.unitId || "";
        this.form.cover = d.cover || "";
        this.form.coverPreview = d.cover || "";
        this.form.fileId = d.fileId || "";
        this.form.zipFileName = d.fileId ? "已上传的附件（重新选择可替换）" : "";
      } catch (e) {
        alert("加载工具信息失败，请稍后重试");
      }
    },
    // 获取分类列表
    async getCategory() {
      try {
        const res = await this.$axios({
          url: "category/list",
        });
        this.categories = res.data;
        // 确保分类有数据时才设置默认值
        if (this.categories.length > 0) {
          this.form.categoryid = this.categories[0].id;
        }

        const sub = await this.$axios({
          url: "subinfo/list",
        });
        this.subList = sub.data;
        if (this.subList.length > 0) {
          this.form.subid = this.subList[this.subList.length - 1].id;
        }

        // 知识体系节点（年段/年级/学期/单元）
        const edu = await this.$axios({ url: "edu/list" });
        this.eduList = edu.data || [];
      } catch (error) {
        console.error("获取分类失败:", error);
        alert("获取分类列表失败，请稍后重试");
      }
    },
    setSub(id) {
      var form = Object.assign({},this.form);
      form.subid = id;
      // 切换学科后重置知识层级
      form.stageId = "";
      form.gradeId = "";
      form.termId = "";
      form.unitId = "";
      this.form = Object.assign({},form);
    },
    onStageChange() {
      this.form.gradeId = "";
      this.form.termId = "";
      this.form.unitId = "";
    },
    onGradeChange() {
      this.form.termId = "";
      this.form.unitId = "";
    },
    onTermChange() {
      this.form.unitId = "";
    },

    // 处理封面文件上传选择
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 验证文件类型和大小
      const isImage = file.type.startsWith("image/");
      const isLt10M = file.size <= imageMaxBytes();

      if (!isImage) {
        this.uploadError = "请上传图片格式的文件（PNG/JPG/GIF）";
        return;
      }

      if (!isLt10M) {
        this.uploadError =
          "图片大小不能超过" + getUploadConfig().imageMaxMB + "MB";
        return;
      }

      // 清除之前的错误提示
      this.uploadError = "";
      // 保存文件对象
      this.form.imageFile = file;
      // 创建预览图片的URL
      const reader = new FileReader();
      reader.onload = (e) => {
        this.form.coverPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // 清除封面图片
    clearCover() {
      this.form.coverPreview = "";
      this.form.imageFile = null;
      this.form.cover = "";
      this.uploadError = "";
      // 重置文件输入框
      document.getElementById("file-upload").value = "";
    },

    // 新增：处理ZIP文件上传选择
    handleZipFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 验证文件类型（确保是zip/html文件）
      const name = (file.name || "").toLowerCase();
      const isZip =
        name.endsWith(".zip") ||
        name.endsWith(".html") ||
        name.endsWith(".htm") ||
        file.type === "application/zip" ||
        file.type === "text/html" ||
        file.type === "application/x-zip-compressed";
      // 验证文件大小（上限由后台「上传设置」配置）
      const isLt100M = file.size <= fileMaxBytes();

      if (!isZip) {
        this.zipUploadError = "仅支持上传ZIP/HTML格式的文件";
        return;
      }

      if (!isLt100M) {
        this.zipUploadError =
          "ZIP/HTML文件大小不能超过" + getUploadConfig().fileMaxMB + "MB";
        return;
      }

      // 清除之前的错误提示
      this.zipUploadError = "";
      // 保存文件信息
      this.form.zipFile = file;
      this.form.zipFileName = file.name;
      this.form.zipFileSize = file.size;

      // 重置文件输入框值（避免重复选择同一文件不触发change事件）
      // document.getElementById("zip-file-upload").value = "";
    },

    // 新增：清除ZIP文件
    clearZipFile() {
      this.form.zipFile = null;
      this.form.zipFileName = "";
      this.form.zipFileSize = 0;
      this.form.zipUrl = "";
      this.zipUploadError = "";
    },

    // 新增：格式化文件大小显示
    formatFileSize(bytes) {
      if (bytes === 0) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    // 上传图片到服务器
    async uploadImage() {
      if (!this.form.imageFile) return true;

      this.uploading = true;
      this.uploadError = "";
      this.uploadPercent = 0;

      try {
        // 创建FormData对象用于文件上传
        const formData = new FormData();
        formData.append("file", this.form.imageFile);
        formData.append("UploadType", "Files");
        formData.append(
          "businessType",
          "cover/" + new Date().format("yyyy/MM/dd")
        );

        // 上传图片到服务器
        const res = await this.$axios({
          url: "upload", // 图片上传接口
          method: "POST",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data", // 必须设置的请求头
          },
          onUploadProgress: (e) => {
            this.uploadPercent = e.total
              ? Math.round((e.loaded / e.total) * 100)
              : 0;
          },
        });

        // 假设服务器返回 { code: 200, data: { url: "https://xxx.com/xxx.jpg" } }
        if (res.code === 200) {
          console.log(JSON.stringify(res));
          this.form.cover = res.data.uploads.files[0].o.fullPath; // 保存服务器返回的图片URL
          this.uploadPercent = 100;
          return true;
        } else {
          this.uploadError = res.message || "图片上传失败";
          return false;
        }
      } catch (error) {
        console.error("图片上传失败:", error);
        this.uploadError = "图片上传失败，请检查网络或稍后重试";
        return false;
      } finally {
        this.uploading = false;
      }
    },

    // 新增：上传ZIP文件到服务器
    async uploadZipFile() {
      if (!this.form.zipFile) return true;

      this.zipUploading = true;
      this.zipUploadError = "";
      this.zipUploadPercent = 0;

      try {
        // 创建FormData对象用于文件上传
        const formData = new FormData();
        formData.append("file", this.form.zipFile);
        formData.append("UploadType", "Files");
        formData.append(
          "businessType",
          "tool/" + new Date().format("yyyy/MM/dd")
        );

        // 上传ZIP文件到服务器
        const res = await this.$axios({
          url: "upload", // ZIP文件上传接口（可复用图片上传接口）
          method: "POST",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data", // 必须设置的请求头
          },
          timeout: 600 * 1000, // 大文件上传放宽超时到 10 分钟
          onUploadProgress: (e) => {
            this.zipUploadPercent = e.total
              ? Math.round((e.loaded / e.total) * 100)
              : 0;
          },
        });

        // 假设服务器返回格式和图片上传一致
        if (res.code === 200) {
          this.form.fileId = res.data.uploads.id; // 保存服务器返回的ZIP文件URL
          this.zipUploadPercent = 100;
          return true;
        } else {
          this.zipUploadError = res.message || "附件上传失败";
          return false;
        }
      } catch (error) {
        console.error("ZIP文件上传失败:", error);
        this.zipUploadError = "附件上传失败，请检查网络或稍后重试";
        return false;
      } finally {
        this.zipUploading = false;
      }
    },

    // 提交表单
    async handleSubmit() {
      // 0. 访客模式：提交前二次校验登录
      if (!requireLogin(this, "发布工具")) return;
      // 1. 先验证必要字段
      if (!this.form.title || !this.form.categoryid) {
        alert("请填写完整的表单信息");
        return;
      }

     

      // 2. 如果有图片文件但还没上传，先上传图片
      let uploadSuccess = true;
      if (this.form.imageFile) {
        uploadSuccess = await this.uploadImage();
        if (!uploadSuccess) {
          return; // 上传失败则终止提交
        }
      }

      // 3. 新增：如果有ZIP文件但还没上传，先上传ZIP文件
      if (this.form.zipFile) {
        uploadSuccess = await this.uploadZipFile();
        if (!uploadSuccess) {
          return; // 上传失败则终止提交
        }
      }

       if (!this.form.cover || this.form.cover.length == 0) {
        alert("请上传封面图片");
        return;
      }
      if (!this.form.fileId || this.form.fileId.length == 0) {
        alert("请上传工具附件");
        return;
      }

      // 4. 提交表单数据到服务器
      try {
        const submitData = {
          id: this.form.id || undefined,
          title: this.form.title,
          categoryid: this.form.categoryid,
          description: this.form.description,
          authorName: (this.form.authorName || "").trim(),
          link: this.form.link,
          cover: this.form.cover, // 提交服务器返回的图片URL
          fileId: this.form.fileId, // 新增：附件ID
          subid: this.form.subid,
          stageId: this.form.stageId || "",
          gradeId: this.form.gradeId || "",
          termId: this.form.termId || "",
          unitId: this.form.unitId || "",
        };
        var data = await this.$aesEncrypt(submitData);

        // 调用发布工具的接口（请替换为你的实际接口）
        this.$axios({
          url: "aitool",
          method: "PUT",
          data: {
            data: data,
          },
        }).then((res) => {
          if (res.code === 200) {
            if (this.form.id) {
              alert("修改已提交，将重新进入审核。");
              this.$router.push("/user");
              return;
            }
            alert("工具提交成功！审核通过后将展示在平台上。");
            this.$router.push("/");
          } else {
            alert("提交失败：" + (res.msg || "服务器错误"));
          }
        });
      } catch (error) {
        console.error("提交表单失败:", error);
        alert("提交失败，请检查网络或稍后重试");
      }
    },
  },
};

// 日期格式化扩展
Date.prototype.format = function (fmt) {
  var o = {
    "M+": this.getMonth() + 1, //月份
    "d+": this.getDate(), //日
    "h+": this.getHours(), //小时
    "m+": this.getMinutes(), //分
    "s+": this.getSeconds(), //秒
    "q+": Math.floor((this.getMonth() + 3) / 3), //季度
    S: this.getMilliseconds(), //毫秒
  };
  if (/(y+)/.test(fmt))
    fmt = fmt.replace(
      RegExp.$1,
      (this.getFullYear() + "").substr(4 - RegExp.$1.length)
    );
  for (var k in o)
    if (new RegExp("(" + k + ")").test(fmt))
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length == 1 ? o[k] : ("00" + o[k]).substr(("" + o[k]).length)
      );
  return fmt;
};
</script>
